import { useEffect, useState } from "react";
import { Home, Users, UserPlus, BadgeCheck, Hourglass, Megaphone, Plus, CalendarPlus, TrendingUp, TrendingDown, Wallet } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";
import { api, compactNumber, formatTL } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { UpcomingWidget } from "@/components/planner/UpcomingWidget";
import { WelcomeHero } from "@/components/dashboard/WelcomeHero";
import { StatCard, Delta } from "@/components/dashboard/StatCard";
import { Panel } from "@/components/dashboard/Panel";
import { RecentPortfolios, RecentCustomers, PendingWork, RecentActivity, Reminders, QuickAccess } from "@/components/dashboard/OfficePanels";
import { EXPENSE_CATS } from "@/pages/FinancePage";

const COLORS = ["#047857", "#2563EB", "#D97706", "#DC2626", "#0891B2", "#65A30D"];

function FinanceBlock({ finance, showCharts }) {
  const box = "rounded-2xl border p-5";
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div data-testid="finance-income" className={`${box} bg-emerald-50/80 border-emerald-200/80`}>
          <div className="flex items-center gap-2 text-emerald-700 text-xs font-semibold mb-2"><TrendingUp className="w-3.5 h-3.5" /> Toplam Gelir</div>
          <div className="text-2xl font-bold text-emerald-900" style={{ fontFamily: "Outfit, sans-serif" }}>{formatTL(finance.total_income)}</div>
        </div>
        <div data-testid="finance-expense" className={`${box} bg-red-50/80 border-red-200/80`}>
          <div className="flex items-center gap-2 text-red-700 text-xs font-semibold mb-2"><TrendingDown className="w-3.5 h-3.5" /> Toplam Gider</div>
          <div className="text-2xl font-bold text-red-900" style={{ fontFamily: "Outfit, sans-serif" }}>{formatTL(finance.total_expense)}</div>
        </div>
        <div data-testid="stat-net" className={`${box} bg-white border-slate-200/80`}>
          <div className="flex items-center gap-2 text-slate-500 text-xs font-semibold mb-2"><Wallet className="w-3.5 h-3.5" /> Net Kâr</div>
          <div className={`text-2xl font-bold ${finance.net_profit >= 0 ? "text-emerald-700" : "text-red-600"}`} style={{ fontFamily: "Outfit, sans-serif" }}>{formatTL(finance.net_profit)}</div>
        </div>
      </div>
      {showCharts && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <Panel title="Aylık Gelir / Gider" testId="chart-monthly">
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={finance.monthly}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                <XAxis dataKey="month" stroke="#64748b" fontSize={11} />
                <YAxis stroke="#64748b" fontSize={11} width={60} tickFormatter={compactNumber} />
                <Tooltip formatter={(v) => formatTL(v)} />
                <Bar dataKey="income" fill="#047857" radius={[6, 6, 0, 0]} />
                <Bar dataKey="expense" fill="#F87171" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Panel>
          <Panel title="Gider Kategorileri" testId="chart-expense-categories">
            {finance.expense_by_category.length === 0 ? <div className="text-slate-400 text-sm">Henüz gider kaydı yok.</div> : (
              <ResponsiveContainer width="100%" height={260}>
                <PieChart>
                  <Pie data={finance.expense_by_category.map((c) => ({ ...c, category: EXPENSE_CATS.find((x) => x.v === c.category)?.l || c.category }))} dataKey="amount" nameKey="category" innerRadius={55} outerRadius={90} label={(e) => e.category}>
                    {finance.expense_by_category.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Pie>
                  <Tooltip formatter={(v) => formatTL(v)} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </Panel>
        </div>
      )}
    </div>
  );
}

function StatGrid({ s }) {
  const activePct = s.total_portfolios ? Math.round((s.active_portfolios / s.total_portfolios) * 100) : 0;
  const cards = [
    { testId: "stat-portfolios", icon: Home, label: "Toplam Portföy", value: s.total_portfolios, hint: "Ofisteki tüm kayıtlar" },
    { testId: "stat-active", icon: Megaphone, label: "Aktif İlanlar", value: s.active_portfolios, tone: "blue", hint: `Portföyün %${activePct}'i yayında` },
    { testId: "stat-customers", icon: Users, label: "Toplam Müşteri", value: s.total_customers, hint: `Bu ay +${s.new_customers} yeni` },
    { testId: "stat-new-customers", icon: UserPlus, label: "Yeni Müşteriler", value: s.new_customers, tone: "blue", hint: <><Delta cur={s.new_customers} prev={s.new_customers_prev} /> geçen aya göre</> },
    { testId: "stat-closed", icon: BadgeCheck, label: "Satılan / Kiralanan", value: s.closed_total, hint: <><Delta cur={s.closed_month} prev={s.closed_prev} /> bu ay {s.closed_month}</> },
    { testId: "stat-pending", icon: Hourglass, label: "Bekleyen İşlemler", value: s.pending_total, tone: "amber", hint: `${s.open_todos} görev · ${s.open_deeds} tapu` },
  ];
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-6 gap-4">
      {cards.map((c, i) => <StatCard key={c.testId} {...c} delay={i * 50} />)}
    </div>
  );
}

export default function DashboardPage() {
  const { user, office, subscription, hasFeature } = useAuth();
  const [data, setData] = useState(null);
  const [finance, setFinance] = useState(null);
  const canSeeFinance = ["BROKER", "ACCOUNTANT"].includes(user?.role) && hasFeature("FINANCE");

  useEffect(() => {
    api.get("/dashboard/overview").then((r) => setData(r.data)).catch(() => {});
    if (canSeeFinance) api.get("/finance/summary").then((r) => setFinance(r.data)).catch(() => {});
  }, [user?.role]);

  const perf = data?.performance;
  const self = perf?.scope === "self";
  const actions = [
    hasFeature("PORTFOLIO") && { to: "/portfolios?new=1", label: "Portföy Ekle", icon: Plus, testId: "hero-add-portfolio" },
    hasFeature("CRM") && { to: "/customers?new=1", label: "Müşteri Kaydı", icon: UserPlus, testId: "hero-add-customer" },
    { to: "/calendar?new=1", label: "Randevu Oluştur", icon: CalendarPlus, testId: "hero-add-appointment" },
  ].filter(Boolean);
  const pending = data?.stats.pending_total ?? 0;

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <WelcomeHero name={user?.full_name?.split(" ")[0]} actions={actions}
        subtitle={data ? (pending ? `${office?.name || "Ofisiniz"} için bugün ${pending} bekleyen işlem var.` : `${office?.name || "Ofisiniz"} için her şey yolunda, bekleyen işlem yok.`) : "Ofisinizin özet görünümü yükleniyor..."}
        gauge={perf && { score: perf.score, percent: perf.percent, label: self ? "Performans puanınız" : "Ofis performans puanı",
          caption: perf.prev ? `Bu ay toplanan puan · Geçen ay: ${perf.prev} puan` : "Bu ay toplanan puan" }} />

      {!data ? <div data-testid="dashboard-loading" className="text-sm text-slate-500">Yükleniyor...</div> : (
        <>
          <StatGrid s={data.stats} />
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            {hasFeature("PORTFOLIO") ? <RecentPortfolios items={data.portfolios} className="lg:col-span-2" /> : <QuickAccess role={user.role} hasFeature={hasFeature} className="lg:col-span-2" />}
            <UpcomingWidget />
            {hasFeature("CRM") && <RecentCustomers items={data.customers} />}
            <PendingWork todos={data.todos} deeds={data.deeds} showDeeds={hasFeature("DEED")} />
            <Reminders todos={data.todos} deeds={hasFeature("DEED") ? data.deeds : []} subscription={subscription} isDemo={office?.is_demo} />
            <RecentActivity items={data.activity} />
            {hasFeature("PORTFOLIO") && <QuickAccess role={user.role} hasFeature={hasFeature} className="lg:col-span-2" />}
          </div>
        </>
      )}

      {canSeeFinance && finance && <FinanceBlock finance={finance} showCharts={hasFeature("REPORTS")} />}
    </div>
  );
}
